/**
 * Customer Performance Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useCustomerPerformance hook.
 */

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Download, Grid3X3, Table, Loader2 } from 'lucide-react';
import CustomerPerformanceFilters from '../components/customers/CustomerPerformanceFilters';
import CustomerPerformanceMatrixView from '../components/customers/CustomerPerformanceMatrixView';
import {
  useCustomerPerformance,
  DrilldownModal,
  PerformanceTable,
} from './customer-performance';

export default function CustomerPerformance() {
  const {
    // Data
    filteredCustomers,
    ratingThresholds,

    // UI states
    loading,
    initialLoad,
    error,
    searchTerm,
    isMatrixView,
    filters,

    // Pagination
    pagination,

    // Modal
    modalOpen,
    modalTitle,
    modalSubtitle,
    modalData,
    modalType,
    modalLoading,
    modalPage,
    modalTotalCount,
    modalTotalPages,

    // Currency
    currency,

    // Setters
    setSearchTerm,
    setModalOpen,

    // Actions
    fetchData,
    handleApplyFilters,
    handleExportExcel,
    handleMatrixView,
    handleModalPageChange,
    openGRNModal,
    openNCRModal,
    openPOLineModal,
  } = useCustomerPerformance();

  if (loading && initialLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading supplier performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customer Performance</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive customer performance metrics and analytics
          </p>
        </div>
      </div>

      {/* Comprehensive Filter Panel */}
      <CustomerPerformanceFilters
        filters={filters}
        ratingThresholds={ratingThresholds}
        onApplyFilters={handleApplyFilters}
      />

      {/* Search and Action Bar */}
      <div className="flex items-center justify-between gap-4">
        {/* Quick Search */}
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* View and Export Actions */}
        <div className="flex items-center gap-2">
          {/* Rows per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Rows:</span>
            <Select
              value={pagination.pageSize.toString()}
              onValueChange={(value) => {
                pagination.setPageSize(parseInt(value));
              }}
            >
              <SelectTrigger className="w-[70px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant={isMatrixView ? 'default' : 'outline'}
            size="sm"
            onClick={handleMatrixView}
          >
            {isMatrixView ? (
              <>
                <Table className="h-4 w-4 mr-2" />
                Table View
              </>
            ) : (
              <>
                <Grid3X3 className="h-4 w-4 mr-2" />
                Matrix View
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Performance Table or Matrix View */}
      {isMatrixView ? (
        <CustomerPerformanceMatrixView
          customers={filteredCustomers}
          ratingThresholds={ratingThresholds}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          itemsPerPage={pagination.pageSize}
          onPageChange={pagination.goToPage}
        />
      ) : (
        <PerformanceTable
          customers={filteredCustomers}
          ratingThresholds={ratingThresholds}
          currency={currency}
          loading={loading}
          pagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalCount: pagination.totalCount,
            pageSize: pagination.pageSize,
            hasMultiplePages: pagination.hasMultiplePages,
            goToPage: pagination.goToPage,
          }}
          onOpenGRNModal={openGRNModal}
          onOpenNCRModal={openNCRModal}
          onOpenPOLineModal={openPOLineModal}
        />
      )}

      {/* Drilldown Modal */}
      <DrilldownModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={modalTitle}
        subtitle={modalSubtitle}
        type={modalType}
        data={modalData}
        loading={modalLoading}
        page={modalPage}
        totalCount={modalTotalCount}
        totalPages={modalTotalPages}
        currency={currency}
        onPageChange={handleModalPageChange}
      />
    </div>
  );
}
